import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import ConditionalButton from "../../../layout/conditional-button";
import ErrorModal from "../../../layout/modal";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import { one as getRole, save as saveRole } from "../../../actions/roles";
import { send as sendAlert } from "../../../actions/alert";

class Detail extends React.Component {
    constructor(props) {
        super(props);
        this.props.loading(true);
        this.state = {
            uuid: props.params.uuid ?? uuidv4(),
            name: "",
            caption: "",
            metaText: "{}",
            modal: {
                error: {
                    show: false,
                },
            },
        };
        this.props.pageInfo({ title: "Role", breadcrumb: `[New Role]` });
    }

    get uuid() {
        return this.props.params.uuid;
    }

    loadData = async () => {
        this.props.loading(true);

        try {
            if (!isGuid(this.uuid)) return;

            const role = await this.props.getRole({ uuid: this.uuid });
            if (!role?.uuid) return;

            this.setState({
                uuid: role.uuid,
                name: role.name || "",
                caption: role.caption || "",
                metaText: JSON.stringify(role.meta ?? {}, null, 2),
            });

            await this.props.pageInfo({
                title: `Role (${role.caption || role.name || "..."})`,
                breadcrumb: `${role.caption || role.name || "Role"}`,
            });
        } finally {
            this.props.loading(false);
        }
    };

    componentDidMount = async () => {
        await this.loadData();
    };

    componentDidUpdate(prevProps) {
        const prevUuid = prevProps.params?.uuid;
        const nextUuid = this.props.params?.uuid;

        if (prevUuid !== nextUuid) {
            this.setState(
                {
                    uuid: nextUuid ?? uuidv4(),
                    name: "",
                    caption: "",
                    metaText: "{}",
                },
                this.loadData
            );
        }
    }

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value,
        });
    };

    validate = () => {
        const errors = {};
        const name = String(this.state.name || "").trim();
        const caption = String(this.state.caption || "").trim();

        if (!name) errors.name = "Role name is required.";
        if (!caption) errors.caption = "Role caption is required.";

        return {
            isValid: Object.keys(errors).length === 0,
            errors,
        };
    };

    onSaveClick = async () => {
        const { isValid, errors } = this.validate();
        if (!isValid) {
            const firstError = Object.values(errors)[0] || "Unable to save role.";
            this.props.sendAlert(firstError, "error");
            return;
        }

        this.props.loading(true);

        try {
            const payload = {
                uuid: isGuid(this.uuid) ? this.uuid : this.state.uuid || uuidv4(),
                name: String(this.state.name || "").trim(),
                caption: String(this.state.caption || "").trim(),
                meta: JSON.parse(this.state.metaText || "{}"),
            };

            const saved = await this.props.saveRole(payload);

            window.location.href = `/dashboard/settings/roles/${saved?.uuid}`;
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to save role.", "error");
        } finally {
            this.props.loading(false);
        }
    };

    onErrorHideModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                error: {
                    show: false,
                },
            },
        }));
    };

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Save Role"
                        display={true}
                        enabled={true}
                        onEnabledClick={this.onSaveClick}
                        icon="save"
                    >
                        Save
                    </ConditionalButton>
                </Col>

                <Col xs="12">
                    <Row>
                        <Col lg="12">
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Role Information</h2>
                                </Col>
                            </Row>

                            <Row className="g-3">
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="caption"
                                            value={this.state.caption || ""}
                                            onChange={this.onChange}
                                        />
                                        <Form.Label>CAPTION</Form.Label>
                                    </div>
                                </Col>

                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={this.state.name || ""}
                                            onChange={this.onChange}
                                        />
                                        <Form.Label>NAME</Form.Label>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>

                <ErrorModal
                    show={this.state.modal.error.show}
                    title={this.state.modal.error.title}
                    body={this.state.modal.error.body}
                    buttons={[
                        {
                            default: true,
                            caption: "ok",
                            onEnabledClick: this.onErrorHideModal,
                            variant: "secondary",
                            enabled: true,
                            display: true,
                        },
                    ]}
                />
            </>
        );
    }
}

Detail.propTypes = {
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default withNavigation(
    connect(mapStateToProps, {
        pageInfo,
        loading,
        getRole,
        saveRole,
        sendAlert,
    })(Detail)
);